import AssignmentIcon from "@mui/icons-material/Assignment";
import BusinessIcon from "@mui/icons-material/Business";
import DescriptionIcon from "@mui/icons-material/Description";
import ExtensionIcon from "@mui/icons-material/Extension";
import GroupIcon from "@mui/icons-material/Group";
import HotelIcon from "@mui/icons-material/Hotel";
import LinkIcon from "@mui/icons-material/Link";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonIcon from "@mui/icons-material/Person";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import SearchIcon from "@mui/icons-material/Search";
import { SEARCH_TYPE_TRANSPORT, TRACK_EVENTS } from "@recare/core/consts";
import {
  hasAccessToReverseSearch,
  newTermsAndConditionsAvailable,
} from "@recare/core/model/careproviders";
import {
  activateNewCapacitySettingsDesign,
  activatePWA,
} from "@recare/core/model/utils/featureFlags";
import {
  Careprovider,
  SidebarMenuItem,
  TrackEventFn,
} from "@recare/core/types";
import { useTranslations } from "@recare/translations";
import { TranslationComposition } from "@recare/translations/helpers";
import Translations from "@recare/translations/types";
import InfoCaption from "ds/components/InfoCaption";
import { SpinnerPage } from "ds/components/Spinner";
import {
  APP_BACKGROUND,
  ICON_GREY,
  PROVIDER_PRIMARY_COLOR,
} from "ds/materials/colors";
import { HorizontalLayout } from "ds/materials/layouts";
import {
  APP_BAR_HEIGHT,
  dp,
  margin,
  padding,
  sizing,
} from "ds/materials/metrics";
import { FONT_SIZE_20 } from "ds/materials/typography";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import SidebarMenu, {
  CAREPROVIDER_SETTINGS_SIDEBAR_WIDTH,
} from "dsl/atoms/SidebarMenu";
import {
  CareproviderNavigationHandlers,
  useCareproviderNavigationHandlers,
} from "dsl/hooks/useNavigationHandlers";
import React, { Suspense, useMemo } from "react";
import { Location, Outlet, useLocation } from "react-router-dom";
import { useTracking } from "react-tracking";
import { useCountry } from "reduxentities/selectors/hooks";
import styled from "styled-components";
import { showExtensionsTab } from "./shared";

export type CareproviderSettingsProps = {
  careprovider: Careprovider;
  children?: React.ReactNode;
};

const Container = styled.div`
  display: flex;
  flex: 1;
  width: 100vw;
`;

const SidebarContainer = styled.div`
  width: ${CAREPROVIDER_SETTINGS_SIDEBAR_WIDTH};
`;

const Background = styled.div`
  display: flex;
  flex: 1;
  width: 100vw;
  min-height: calc(100vh - ${dp(APP_BAR_HEIGHT)});
  background-color: ${APP_BACKGROUND};
`;

const PageContainer = styled.div<{
  isMobile: boolean;
}>`
  display: flex;
  flex: 1;
  justify-content: center;
  padding: ${({ isMobile }) => (isMobile ? padding(0, 0, 3) : padding(3, 0))};
`;

const PageContentContainer = styled.div<{
  isMobile: boolean;
}>`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${({ isMobile }) => (isMobile ? padding(2) : padding(3))};
  overflow: hidden;
`;

const ScrollY = styled.div`
  width: 100%;
  height: calc(100vh - ${dp(APP_BAR_HEIGHT)});
  overflow-y: auto;
`;

export const FieldContainer = styled.div<{
  alignItems?: string;
  justify?: string;
  margin?: string;
  width?: string;
}>`
  display: flex;
  flex: 1 1 auto;
  flex-direction: row;
  justify-content: ${(props) => props.justify || undefined};
  align-items: ${(props) => props.alignItems || "flex-end"};
  width: ${(props) => props.width || undefined};
  margin: ${(props) => props.margin || margin(0)};
`;

export const iconStyle = {
  fontSize: FONT_SIZE_20,
  color: ICON_GREY,
  margin: margin(0, 0, 2, 0),
};

const StyledHint = styled.div`
  position: relative;
`;

export function HintWrapper({
  caption,
  children = undefined,
  desktopOnly = false,
  disconnected = false,
  disabled = false,
  hintMargin,
}: {
  caption: JSX.Element | string;
  children?: React.ReactNode;
  desktopOnly?: boolean;
  disabled?: boolean;
  disconnected?: boolean;
  hintMargin?: string;
}) {
  const { isDesktop, isMobile, isTablet } = useMedia();
  const hintWidth = () => {
    if (isMobile) return sizing(32);
    if (isTablet) return sizing(40);
    if (isDesktop) return sizing(36);
  };
  if (disabled || (desktopOnly && !isDesktop)) {
    return <>{children}</>;
  }
  return (
    <StyledHint>
      {children}
      <HorizontalLayout
        width={hintWidth()}
        style={
          isDesktop
            ? {
                position: "absolute",
                top: sizing(disconnected ? -5 : 1.5),
                left: sizing(60),
                margin: hintMargin,
              }
            : { margin: margin(0, 0, 0, 4.5) }
        }
      >
        <InfoCaption>{caption}</InfoCaption>
      </HorizontalLayout>
    </StyledHint>
  );
}

export function getTranslationCompositionHint(
  translation: string,
  onClick?: () => void,
) {
  return (
    <TranslationComposition translations={translation} withOptions>
      {([before, selectEmail, after]) => (
        <span>
          {before}
          <span
            onClick={onClick}
            style={
              onClick
                ? {
                    cursor: "pointer",
                    fontWeight: "bold",
                    color: PROVIDER_PRIMARY_COLOR,
                  }
                : undefined
            }
          >
            {selectEmail}
          </span>
          {after}
        </span>
      )}
    </TranslationComposition>
  );
}

function getMenuItems({
  careprovider,
  isTransport,
  navigationHandlers,
  showExtensionsTab,
  showNotification,
  trackEvent,
  translations,
}: {
  careprovider: Careprovider;
  isTransport: boolean;
  navigationHandlers: CareproviderNavigationHandlers;
  showExtensionsTab?: boolean;
  showNotification?: boolean;
  trackEvent: TrackEventFn;
  translations: Translations;
}): Array<SidebarMenuItem> {
  return [
    {
      key: "account",
      label: translations.careproviderApp.settings.sidebar.myAccount,
      action: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_ACCOUNT });
        navigationHandlers.goToCareproviderSettingsAccount();
      },
      icon: <PersonIcon color="primary" />,
    },
    {
      key: "profile",
      label: translations.careproviderApp.settings.sidebar.companyProfile,
      action: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_COMPANY });
        navigationHandlers.goToCareproviderSettingsCompanyProfile();
      },
      icon: <BusinessIcon color="primary" />,
    },
    {
      key: "services",
      label: translations.careproviderApp.settings.sidebar.services,
      action: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_SERVICES });
        navigationHandlers.goToCareproviderSettingsServices();
      },
      icon: <AssignmentIcon color="primary" />,
      deactivated: isTransport,
    },
    {
      key: "team",
      label: translations.careproviderApp.settings.sidebar.team,
      action: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_TEAM });
        navigationHandlers.goToCareproviderSettingsTeam();
      },
      icon: <GroupIcon color="primary" />,
    },
    {
      key: "partner_clinics",
      label: translations.careproviderApp.settings.sidebar.partnerClinics,
      action: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_PARTNER_CLINICS,
        });
        navigationHandlers.goToCareproviderSettingsPartnerClinics();
      },
      icon: <LinkIcon color="primary" />,
    },
    {
      key: "legal_documents",
      label: translations.careproviderApp.settings.sidebar.legal,
      action: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_LEGAL });
        navigationHandlers.goToCareproviderSettingsLegalDocuments();
      },
      icon: <DescriptionIcon color="primary" />,
      showNotification,
    },
    ...(showExtensionsTab
      ? [
          {
            key: "extensions",
            label: translations.careproviderApp.settings.extensions.title,
            action: () => {
              trackEvent({
                name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_EXTENSIONS,
              });
              navigationHandlers.goToCareproviderSettingsExtensions();
            },
            icon: <ExtensionIcon color="primary" />,
          },
        ]
      : []),
    {
      key: "notifications",
      label: translations.careproviderApp.settings.sidebar.notifications,
      action: () => {
        trackEvent({
          name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_NOTIFICATIONS,
        });
        navigationHandlers.goToCareproviderSettingsNotifications();
      },
      icon: <NotificationsIcon color="primary" />,
    },
    ...(activatePWA
      ? [
          {
            key: "pwa",
            label: "PWA (beta)",
            action: () => {
              trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_PWA });
              navigationHandlers.goToCareproviderSettingsPWA();
            },
            icon: <PhoneAndroidIcon color="primary" />,
          },
        ]
      : []),
    ...(hasAccessToReverseSearch(careprovider)
      ? [
          {
            key: "search",
            label: translations.careproviderApp.settings.sidebar.search,
            action: () => {
              trackEvent({
                name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_SEARCH,
              });
              navigationHandlers.goToCareproviderSettingsSearch();
            },
            icon: <SearchIcon color="primary" />,
          },
        ]
      : []),
    {
      key: "capacity",
      label: translations.careproviderApp.settings.sidebar.ourCapacity,
      action: () => {
        trackEvent({ name: TRACK_EVENTS.CAREPROVIDER_SETTINGS_TAB_CAPACITY });
        navigationHandlers.goToCareproviderSettingsCapacity();
      },
      icon: <HotelIcon color="primary" />,
    },
  ];
}

function getActiveItem(
  itemsArray: Array<SidebarMenuItem>,
  location: Location | undefined = { pathname: "" } as Location,
) {
  const path = location.pathname;
  let active = undefined;
  itemsArray.forEach(({ key }: { key: string }, i: number) => {
    if (path.includes(key)) active = i;
  });
  return active;
}

function configureSteps({
  careprovider,
  isTransport,
  location,
  navigationHandlers,
  showExtensionsTab,
  showNotification,
  trackEvent,
  translations,
}: {
  careprovider: Careprovider;
  isTransport: boolean;
  location: Location | undefined;
  navigationHandlers: CareproviderNavigationHandlers;
  showExtensionsTab?: boolean;
  showNotification?: boolean;
  trackEvent: TrackEventFn;
  translations: Translations;
}): {
  activeMenuItem: number | undefined;
  menuItems: Array<SidebarMenuItem>;
} {
  const menuItemsList = getMenuItems({
    careprovider,
    navigationHandlers,
    translations,
    isTransport,
    showExtensionsTab,
    showNotification,
    trackEvent,
  });
  const menuItems = menuItemsList.filter((item) => !item.deactivated);
  const activeMenuItem = getActiveItem(menuItems, location);

  return {
    menuItems,
    activeMenuItem,
  };
}

export default function CareproviderSettings({
  careprovider,
  children,
}: CareproviderSettingsProps) {
  const navigationHandlers = useCareproviderNavigationHandlers();
  const { trackEvent } = useTracking();
  const location = useLocation();
  const translations = useTranslations();
  const { isDesktop, isMobile } = useMedia();
  const country = useCountry();

  const isTransport = careprovider?.patient_type === SEARCH_TYPE_TRANSPORT;
  const newerTermsAndConditionsVersionAvailable =
    newTermsAndConditionsAvailable({
      version: careprovider.terms_and_conditions?.version,
      country,
    });
  const showNotification =
    newerTermsAndConditionsVersionAvailable ||
    !careprovider.data_processing_agreement;

  const { activeMenuItem, menuItems } = useMemo(
    () =>
      configureSteps({
        careprovider,
        navigationHandlers,
        translations,
        location,
        trackEvent,
        isTransport,
        showExtensionsTab: showExtensionsTab({ careprovider }),
        showNotification,
      }),
    [location, translations, showNotification],
  );

  if (activateNewCapacitySettingsDesign) {
    return (
      <Background>
        <Container>
          {isDesktop && (
            <SidebarContainer>
              <SidebarMenu
                menuItems={menuItems}
                activeMenuItem={activeMenuItem}
              />
            </SidebarContainer>
          )}
          <PageContentContainer isMobile={isMobile}>
            <Suspense
              fallback={
                <SpinnerPage id="suspense-careprovider-router-settings" />
              }
            >
              {children ? children : <Outlet />}
            </Suspense>
          </PageContentContainer>
        </Container>
      </Background>
    );
  }

  return (
    <Background>
      {isDesktop && (
        <SidebarMenu menuItems={menuItems} activeMenuItem={activeMenuItem} />
      )}
      <ScrollY>
        <PageContainer isMobile={isMobile}>
          <Suspense
            fallback={
              <SpinnerPage id="suspense-careprovider-router-settings" />
            }
          >
            {children ? children : <Outlet />}
          </Suspense>
        </PageContainer>
      </ScrollY>
    </Background>
  );
}
